/**
 * The portal's two views, one per Firebase project: dev-tier tenants
 * (salesguru-dev) and production tenants (salesguru-86316). backend-tenant
 * owns tier membership (src/config/tenantTiers.js); the portal only names the
 * tier it wants via `?tier=`.
 */
export enum TenantTier {
  Prod = 'prod',
  Dev = 'dev'
}

/**
 * Parses REACT_APP_SERVED_TIERS (comma-separated, e.g. "prod,dev") into the
 * tiers this deployment's backend-tenant serves. NO DEFAULT, deliberately: the
 * dev and staging stages answer 400 TIER_NOT_SERVED for `?tier=prod`, so a
 * build that guessed wrong would show a broken Prod view. A missing, empty or
 * unrecognised value throws at startup, like resolveFirebaseMode.
 */
export function parseServedTiers(raw: string | undefined): TenantTier[] {
  const entries = (raw ?? '').split(',').map((entry) => entry.trim()).filter((entry) => entry.length > 0);
  const hasInvalidEntry = entries.some((entry) => !isTenantTier(entry));
  if (entries.length === 0 || hasInvalidEntry) {
    throw new Error(
      `REACT_APP_SERVED_TIERS must be a comma-separated list of "prod" and/or "dev" (got ${raw === undefined ? 'undefined' : `"${raw}"`}). ` +
        'Set it in this stage\'s .env file.'
    );
  }
  return Array.from(new Set(entries)) as TenantTier[];
}

/** Tiers this build's backend serves. Read at import time. */
export const SERVED_TENANT_TIERS: ReadonlyArray<TenantTier> = parseServedTiers(process.env.REACT_APP_SERVED_TIERS);

export const IS_PROD_TIER_SERVED = SERVED_TENANT_TIERS.includes(TenantTier.Prod);

/** Prod when served (the real customers), otherwise the first served tier. */
export const DEFAULT_TENANT_TIER: TenantTier = IS_PROD_TIER_SERVED ? TenantTier.Prod : SERVED_TENANT_TIERS[0];

/** Switch order and copy. Prod first: it is the default and the real customers. */
const ALL_TENANT_TIER_OPTIONS: ReadonlyArray<{ tier: TenantTier; label: string; description: string }> = [
  { tier: TenantTier.Prod, label: 'Prod', description: 'Production clients' },
  { tier: TenantTier.Dev, label: 'Dev', description: 'Internal dev, demo and Apexia tenants' }
];

/** Only the options this deployment can actually serve. */
export const TENANT_TIER_OPTIONS = ALL_TENANT_TIER_OPTIONS.filter((option) =>
  SERVED_TENANT_TIERS.includes(option.tier)
);

/** Remembers the viewer's last tier across reloads (a per-browser convenience). */
export const TENANT_TIER_STORAGE_KEY = 'superadmin.tenantTier';

export function isTenantTier(value: unknown): value is TenantTier {
  return Object.values(TenantTier).includes(value as TenantTier);
}

/** Clamps any tier (e.g. a stored one) to a served tier. */
export function clampToServedTier(value: unknown): TenantTier {
  return isTenantTier(value) && SERVED_TENANT_TIERS.includes(value) ? value : DEFAULT_TENANT_TIER;
}
